/* eslint-disable max-len */
import { h, Fragment } from 'preact';
import MenuIcon from 'utils/icon/icon-menu.svg';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from 'preact/hooks';
// eslint-disable-next-line import/no-extraneous-dependencies
import throttle from 'lodash.throttle';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { formatProduct } from 'utils/storefront/format-resource';
import ToastContext from 'utils/toast/toast-context';
import HeaderButtons from './header-buttons';
import HeaderNav from './header-nav';
import HeaderPromo from './header-promo';

const Header = ({
  navigation,
  mobileMenu,
  logo,
  altLogo,
  background,
  root,
  buttons,
  homePageTransparent,
  homePageTextColor,
  promoBar,
  customer,
  onlyIcons,
  giftCardEnabled,
}) => {
  const scrollOffset = useRef(window.scrollY);
  const container = useRef(null);
  const toast = useContext(ToastContext);
  const storefront = useMemo(() => new Storefront(), []);
  const { containerClass, headerClass } = useMemo(() => {
    const classNames = {
      containerClass: '',
    };
    if (
      homePageTransparent &&
      document.body.classList.contains('template-index')
    ) {
      if (homePageTextColor === 'black') {
        classNames.headerClass +=
          ' [&:not(.scrolled,.mega-menu)+*]:text-black [&:not(.scrolled,.mega-menu)_*]:text-black [&:not(.scrolled,.mega-menu)_.cart-counter]:text-white';
      } else {
        classNames.headerClass +=
          ' [&:not(.scrolled,.mega-menu)+*]:text-white [&:not(.scrolled,.mega-menu)_*]:text-white [&:not(.scrolled,.mega-menu)_.cart-counter]:bg-white [&:not(.scrolled,.mega-menu)_.cart-counter]:text-black transparent-index--text-white';
      }
      classNames.headerClass +=
        ' [&:not(.scrolled,.mega-menu)+*]:!bg-transparent [&:not(.scrolled,.mega-menu)]:!bg-transparent transparent-index';
      classNames.containerClass += 'absolute w-full';
      if (altLogo) {
        classNames.headerClass +=
          ' [&:not(.scrolled,.mega-menu)_.main-logo]:hidden [&:not(.scrolled,.mega-menu)_.alt-logo]:block';
      }
    }
    return classNames;
  }, [homePageTransparent, homePageTextColor, altLogo]);

  const handleScroll = useCallback(
    throttle(() => {
      if (window.scrollDisabled) {
        return;
      }
      const { offsetTop: headerOffset } = container.current.parentElement;

      const { offsetHeight: headerHeight } = container.current.parentElement;

      const { scrollY } = window;
      if (scrollY <= headerOffset) {
        container.current.classList.remove('scrolled');
        scrollOffset.current = 0;
      }
      container.current.parentElement.style.minHeight = `${headerHeight}px`;

      if (scrollY >= headerHeight + headerOffset) {
        container.current.classList.add('scrolled');
      }

      const body = document.querySelector('body');

      if (scrollY > scrollOffset.current) {
        root.parentElement.classList.add('!-top-full');
        body.classList.remove('scroll-down');
      } else {
        root.parentElement.classList.remove('!-top-full');
        body.classList.add('scroll-down');
      }
      scrollOffset.current = scrollY;
    }, 400),
    [container],
  );

  useEffect(() => {
    const swymAddHandle = async (e) => {
      const { d } = e.detail;
      let productTitle = window.TMW.Strings.general.addedToWishlist;
      let productImage = null;
      if (d?.empi) {
        const response = await storefront.request({
          query: queries.getProductById,
          variables: {
            id: `gid://shopify/Product/${d.empi}`,
          },
        });

        if (response.data?.product) {
          const formattedProduct = formatProduct(response.data?.product);
          productTitle =
            window.TMW.Strings.general.addedProductToWishlist.replace(
              '{{product}}',
              `<b>${formattedProduct.title}</b>`,
            );

          if (formattedProduct.images?.[0]) {
            productImage = formattedProduct.images?.[0]?.src;
          }
        }
      }

      let outputMessage =
        '<div class="gap-12 flex flex-row items-center relative top-2">';
      if (productImage) {
        outputMessage += `<img src="${productImage}" class="object-cover w-50">`;
      }
      outputMessage += `<div class="body-3 flex-1">${productTitle}</div>`;
      outputMessage += '</div>';

      toast.open({
        type: 'success',
        background: '#FFFFFF',
        icon: false,
        duration: 2500,
        position: {
          x: 'right',
          y: 'top',
        },
        message: outputMessage,
      });
    };
    const swymRemoveHandle = async (e) => {
      const { d } = e.detail;
      let productTitle = window.TMW.Strings.general.removedFromWishlist;
      if (d?.empi) {
        const response = await storefront.request({
          query: queries.getProductById,
          variables: {
            id: `gid://shopify/Product/${d.empi}`,
          },
        });

        if (response.data?.product) {
          productTitle =
            window.TMW.Strings.general.removedProductFromWishlist.replace(
              '{{product}}',
              `<b>${response.data?.product.title}</b>`,
            );
        }
      }

      toast.open({
        type: 'success',
        background: '#FFFFFF',
        icon: false,
        duration: 2000,
        position: {
          x: 'right',
          y: 'top',
        },
        message: `<div class="gap-12 flex flex-row relative top-2"><svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              class="w-20 h-20"
              >
              <path d="M6.887 3.443c2.361 0 4.264 1.968 4.92 2.952.655-.984 2.558-2.952 4.919-2.952 2.951 0 4.92 1.968 4.92 4.92 0 4.427-6.396 9.346-9.84 12.79-3.443-3.444-9.838-8.363-9.838-12.79 0-2.952 1.968-4.92 4.92-4.92Z"
              stroke="currentColor"></path>
            </svg>
            <div class="body-3 flex-1">${productTitle}</div>
            </div>`,
      });
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('swym_removed', swymRemoveHandle);
    document.addEventListener('swym_added', swymAddHandle);

    document.dispatchEvent(new CustomEvent('headerDomReady'));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('swym_removed', swymRemoveHandle);
      document.removeEventListener('swym_added', swymRemoveHandle);
    };
  }, []);

  return (
    <>
      <HeaderPromo
        promoBar={promoBar}
        customer={customer}
        onlyIcons={onlyIcons}
        giftCardEnabled={giftCardEnabled}
      />
      <div className={containerClass}>
        <div
          ref={container}
          style={{ background }}
          className={headerClass}
          onMouseEnter={() => container.current?.classList.add('mega-menu')}
          onMouseLeave={() => container.current?.classList.remove('mega-menu')}
        >
          <div className="!flex tablet:container ml:tablet:container tablet:!gap-x-0 mx-auto justify-between items-center px-24 py-20 ml:px-16 tablet:py-0">
            <button
              className="tablet:hidden"
              id="mobile-search-overlay-toggle"
              onClick={() =>
                document.dispatchEvent(new CustomEvent('openMobileMenu'))
              }
            >
              <MenuIcon className="w-40 h-auto" />
            </button>
            <a
              className="display-1 text-primary-1 logo [&_svg]:block ml:[&_svg]:h-14 [&_svg]:max-h-18 [&_svg]:w-auto ml:tablet:[&_svg]:h-16 ml:md:[&_svg]:h-20"
              href={Shopify.routes.root}
            >
              <div
                dangerouslySetInnerHTML={{ __html: logo }}
                className="main-logo"
              ></div>
              {!!altLogo && (
                <div
                  dangerouslySetInnerHTML={{ __html: altLogo }}
                  className="alt-logo hidden [&_svg]:block ml:[&_svg]:h-14 [&_svg]:h-18 [&_svg]:w-auto ml:tablet:[&_svg]:h-16 ml:md:[&_svg]:h-20"
                ></div>
              )}
            </a>
            <HeaderNav
              className="hidden tablet:block"
              links={navigation}
              background={background}
              listClass="flex flex-wrap content-center lg:mx-28"
              itemClass="py-20 pr-16 group-last:pr-0 lg:pr-28"
              withMegaMenu
            />
            <HeaderButtons buttons={buttons} />
          </div>
        </div>
        <HeaderNav
          style={{ background }}
          className="ease-in duration-0 tablet:duration-300 tablet:hidden overflow-x-auto w-full [.template-product_&]:hidden"
          links={mobileMenu}
          listClass="flex px-24 ml:px-16 pt-4 pb-18 inline-flex left-half -translate-x-half relative max-w-full lg:flex lg:static"
          itemClass="whitespace-nowrap pr-32 md:pr-40 first:ml-auto last:mr-auto"
        />
      </div>
    </>
  );
};

export default Header;
